import {
    faCircleQuestion,
    faFile,
    faHandHoldingMedical,
    faHeartPulse,
    faPersonFalling,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChipProps } from '@mui/material';

import { HospiceStatus } from 'src/constants/HospiceStatus';
import {
    ResuscitationStrategies,
    ResuscitationStrategyType,
} from 'src/constants/ResuscitationStrategies';

/**
 * Visual style props for a `<Chip>` variant. Centralizes the appearance
 * (color, variant, icon, size) of each named chip type used in the app so
 * any future tweak to a chip's look happens here rather than at the call
 * site. Spread the matching constant onto a `<Chip>` to apply it:
 *
 * ```tsx
 * <Chip label="DNR" {...ResuscitationChipStyles.DNR} />
 * ```
 *
 * `label`, click handlers, and any per-instance overrides (e.g. `sx`)
 * still live at the call site.
 */
export type ChipStyle = Partial<Pick<ChipProps, 'color' | 'variant' | 'icon' | 'size' | 'sx'>>;

// ---------------------------------------------------------------------
// Hospice / Standard Care
// ---------------------------------------------------------------------

export const HospiceChipStyles: Record<HospiceStatus, ChipStyle> = {
    [HospiceStatus.HOSPICE_CARE]: {
        color: 'primaryInverse',
        variant: 'filled',
        icon: <FontAwesomeIcon icon={faHandHoldingMedical} />,
    },
    [HospiceStatus.STANDARD_CARE]: {
        color: 'primaryInverse',
        variant: 'filled',
    },
    [HospiceStatus.UNKNOWN]: {
        color: 'errorInverse',
        variant: 'filled',
        icon: <FontAwesomeIcon icon={faCircleQuestion} />,
    },
};

// ---------------------------------------------------------------------
// Resuscitation / Code Status
// ---------------------------------------------------------------------

export const ResuscitationChipStyles: Record<ResuscitationStrategyType, ChipStyle> = {
    [ResuscitationStrategies.DNR]: {
        color: 'neutral',
        variant: 'filled',
        icon: <FontAwesomeIcon icon={faFile} />,
    },
    [ResuscitationStrategies.CPR]: {
        color: 'primary',
        variant: 'outlined',
        icon: <FontAwesomeIcon icon={faHeartPulse} />,
    },
    [ResuscitationStrategies.UNKNOWN]: {
        color: 'errorInverse',
        variant: 'filled',
        icon: <FontAwesomeIcon icon={faCircleQuestion} />,
    },
};

// ---------------------------------------------------------------------
// Care Outcome
// ---------------------------------------------------------------------

/**
 * Discriminator for `CareOutcomeChipStyles`: a care outcome is either a
 * refusal or provided care. Kept separate from the API's full
 * `CareOutcome.outcome` enum because only the refusal distinction drives
 * the chip's appearance today.
 */
export type CareOutcomeChipVariant = 'REFUSED' | 'PROVIDED';

export const CareOutcomeChipStyles: Record<CareOutcomeChipVariant, ChipStyle> = {
    REFUSED: {
        color: 'error',
        variant: 'outlined',
        sx: { backgroundColor: 'common.white' },
    },
    PROVIDED: {
        color: 'neutral',
        variant: 'outlined',
        sx: { backgroundColor: 'common.white' },
    },
};

// ---------------------------------------------------------------------
// Observation
// ---------------------------------------------------------------------

export const ObservationChipStyle: ChipStyle = {
    color: 'warningInverse',
    variant: 'filled',
};

export const OutlinedObservationChipStyle: ChipStyle = {
    color: 'warning',
    variant: 'outlined',
    sx: { backgroundColor: 'common.white' },
};

// ---------------------------------------------------------------------
// Fall Resolution
// ---------------------------------------------------------------------

export type FallResolutionChipVariant = 'fall' | 'no-fall';

export const FallResolutionChipStyles: Record<FallResolutionChipVariant, ChipStyle> = {
    fall: {
        color: 'error',
        icon: <FontAwesomeIcon icon={faPersonFalling} />,
    },
    'no-fall': {
        variant: 'outlined',
    },
};

// ---------------------------------------------------------------------
// On / Off status
// ---------------------------------------------------------------------

export type OnOffChipVariant = 'on' | 'off';

export const OnOffChipStyles: Record<OnOffChipVariant, ChipStyle> = {
    on: {
        color: 'success',
        variant: 'filled',
    },
    off: {
        variant: 'outlined',
    },
};
